import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import gsap from 'gsap';
import '../landing.css';
import '../core-pages.css';
import '../themes.css';
import { Lock, ArrowRight, RefreshCw, CheckCircle2, X, Layout, ShieldCheck, BarChart2 } from 'lucide-react';
import { supabase, fetchAllUsernames } from '../lib/supabase';
import {
  getPasswordResetRedirectTo,
  isPasswordRecoveryUrl,
  rememberPasswordResetEmail,
  hasPasswordRecoveryState,
} from '../lib/authRecovery';
import { BloomFilter } from '../lib/bloomFilter';
import { getSecurityCapabilities } from '../lib/securityFlow';
import { loginWithPasskey } from '../lib/passkeyAuth';
import { waitForRouteTransition } from '../lib/transitionDelay';
import { isReservedProfileSlug } from '../lib/reservedSlugs';
import { getProfileTemplate, isKnownProfileTemplate } from '../lib/profileTemplates';
import PasswordResetModal from '../components/PasswordResetModal';
import { toast } from '../lib/feedbackToast';
import { BrutalButton, BrutalInput, BrutalCard } from '../components/BrutalUI';
import { cn } from '../lib/utils';

const GoogleLogo = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const GithubLogo = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" />
  </svg>
);

const OAUTH_INVITE_STORAGE_KEY = 'linkit_pending_oauth_invite_code';
const PASSWORD_AUTH_STAGES = [
  { id: 'checking_account', label: 'Checking account' },
  { id: 'verifying_credentials', label: 'Verifying credentials' },
  { id: 'preparing_vault', label: 'Preparing vault access' },
  { id: 'opening_studio', label: 'Opening your studio' },
];
const PASSKEY_AUTH_STAGES = [
  { id: 'preparing_passkey', label: 'Preparing challenge' },
  { id: 'waiting_passkey', label: 'Waiting for device approval' },
  { id: 'verifying_passkey', label: 'Verifying passkey' },
  { id: 'creating_session', label: 'Opening secure session' },
];

const AuthProgress = ({ steps, activeStage, failed = false, tone = 'light' }) => {
  const activeIndex = steps.findIndex(step => step.id === activeStage);

  return (
    <div className={cn('auth-progress', tone === 'dark' && 'auth-progress-dark')} aria-live="polite">
      {steps.map((step, index) => {
        const complete = activeStage === 'complete' || index < activeIndex;
        const active = index === activeIndex && !failed;
        const stepFailed = failed && index === Math.max(activeIndex, 0);

        return (
          <div
            key={step.id}
            className={cn(
              'auth-progress-step',
              complete && 'auth-progress-step-complete',
              active && 'auth-progress-step-active',
              stepFailed && 'auth-progress-step-failed',
            )}
          >
            <span className="auth-progress-marker">
              {complete ? <CheckCircle2 size={14} /> : active ? <RefreshCw size={13} className="animate-spin" /> : index + 1}
            </span>
            <span>{step.label}</span>
          </div>
        );
      })}
    </div>
  );
};

const Login = () => {
  const [searchParams] = useSearchParams();
  const intentUser = searchParams.get('intent');
  const requestedTemplate = searchParams.get('template');
  const templateSlug = isKnownProfileTemplate(requestedTemplate) ? requestedTemplate : null;
  const template = getProfileTemplate(templateSlug);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState(intentUser || '');
  const [inviteCode, setInviteCode] = useState(searchParams.get('code') || '');
  const [oauthInviteCode, setOauthInviteCode] = useState('');
  const [oauthInviteProvider, setOauthInviteProvider] = useState(null);
  const [isOAuthInviteOpen, setIsOAuthInviteOpen] = useState(false);
  const [isCheckingInvite, setIsCheckingInvite] = useState(false);
  const [loading, setLoading] = useState(false);
  const [authStage, setAuthStage] = useState('idle');
  const [isSignUp, setIsSignUp] = useState(!!intentUser || searchParams.get('signup') === 'true');
  const [bloom, setBloom] = useState(null);
  const [availability, setAvailability] = useState('idle');
  const [capabilities, setCapabilities] = useState(null);
  const [isPasswordResetOpen, setIsPasswordResetOpen] = useState(false);
  const [passkeyStage, setPasskeyStage] = useState('idle');
  const navigate = useNavigate();

  const getPostAuthPath = React.useCallback(() => {
    const redirectPath = searchParams.get('redirect');
    const target = new URL(redirectPath || '/dashboard', window.location.origin);
    if (templateSlug && target.pathname.startsWith('/dashboard')) {
      target.searchParams.set('template', templateSlug);
    }
    return `${target.pathname}${target.search}${target.hash}`;
  }, [searchParams, templateSlug]);

  // Show any OAuth error that came back from the callback redirect
  useEffect(() => {
    const oauthError = searchParams.get('error');
    if (oauthError) {
      toast.error(decodeURIComponent(oauthError));
    }

    // Check for password reset trigger
    if (isPasswordRecoveryUrl() || hasPasswordRecoveryState()) {
      setIsPasswordResetOpen(true);
    }
  }, [searchParams]);

  useEffect(() => {
    getSecurityCapabilities().then(setCapabilities).catch(() => setCapabilities({}));
  }, []);

  // Refs for GSAP
  const containerRef    = useRef(null);
  const logoRef         = useRef(null);
  const cardRef         = useRef(null);
  const usernameFieldRef = useRef(null);
  const hasAnimatedIn   = useRef(false);

  // ── Stealth Duress Protocol (Shadow Routing) ──────────────────────────────
  useEffect(() => {
    let keyBuffer = '';
    const secretCode = 'DURESS';

    const handleKeyDown = (e) => {
      keyBuffer += e.key.toUpperCase();
      if (keyBuffer.length > secretCode.length) {
        keyBuffer = keyBuffer.slice(1);
      }
      if (keyBuffer === secretCode) {
        // Silently activate Shadow Routing
        localStorage.setItem('_linkit_shadow_routing_active', 'true');
        // Visual cue: subtle blink on the logo
        gsap.to(logoRef.current, { opacity: 0.5, duration: 0.1, yoyo: true, repeat: 3 });
        keyBuffer = '';
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // ── Page entrance animation ─────────────────────────────────────────────────
  useEffect(() => {
    if (hasAnimatedIn.current) return;
    hasAnimatedIn.current = true;

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

      tl.from(logoRef.current, {
        y: -12,
        opacity: 0,
        duration: 0.35,
      }, 0);

      tl.from(cardRef.current, {
        y: 18,
        opacity: 0,
        duration: 0.45,
      }, 0.08);

      tl.from('.login-field', {
        y: 8,
        opacity: 0,
        duration: 0.28,
        stagger: 0.045,
      }, 0.18);

      tl.from('.login-oauth-section', {
        y: 8,
        opacity: 0,
        duration: 0.3,
      }, 0.28);

      tl.from('.login-toggle-text', {
        opacity: 0,
        duration: 0.25,
      }, 0.34);

    }, containerRef);

    return () => ctx.revert();
  }, []);

  // ── Username field reveal animation (when toggling Sign Up) ─────────────────
  useEffect(() => {
    const field = usernameFieldRef.current;
    if (!field) return;

    if (isSignUp) {
      gsap.fromTo(field,
        { opacity: 0, y: -6 },
        {
          opacity: 1,
          y: 0,
          duration: 0.28,
          ease: 'power3.out',
        }
      );
    }
  }, [isSignUp]);

  // ── Data & Auth logic (unchanged) ─────────────────────────────────────────
  React.useEffect(() => {
    if (isSignUp && !bloom) {
      const initBloom = async () => {
        const data = await fetchAllUsernames();
        if (data && data.length > 0) {
          const bf = new BloomFilter();
          bf.addAll(data);
          setBloom(bf);
        }
      };
      initBloom();
    }
  }, [isSignUp, bloom]);

  const checkUsername = React.useCallback(async (val) => {
    if (!val || val.length < 3) { setAvailability('idle'); return; }
    if (isReservedProfileSlug(val)) { setAvailability('taken'); return; }
    if (!bloom) return;

    const maybeTaken = bloom.check(val);
    if (!maybeTaken) { setAvailability('available'); return; }

    setAvailability('verifying');
    const { data } = await supabase
      .from('profiles')
      .select('username')
      .eq('username', val.toLowerCase())
      .single();

    setAvailability(data ? 'taken' : 'available');
  }, [bloom]);

  React.useEffect(() => {
    const timer = setTimeout(() => { if (isSignUp) checkUsername(username); }, 300);
    return () => clearTimeout(timer);
  }, [username, isSignUp, checkUsername]);

  const handleResetPassword = async () => {
    if (!email) {
      toast.error('Email required', { description: 'Please enter your email address first.' });
      return;
    }
    setLoading(true);
    // Remember this email for the security handshake later
    rememberPasswordResetEmail(email);
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: getPasswordResetRedirectTo(),
    });

    if (error) {
      toast.error('Password reset failed', { description: error.message });
    } else {
      toast.success('Security link sent!', { description: 'Please check your email for the next steps.' });
    }
    setLoading(false);
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    setAuthStage('checking_account');
    try {
      if (isSignUp) {
        const signupResponse = await fetch('/api/v1/invite-signup', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            password,
            username,
            invite_code: inviteCode,
            template: templateSlug || undefined,
          }),
        });
        const signupPayload = await signupResponse.json().catch(() => ({}));
        if (!signupResponse.ok) throw new Error(signupPayload.error || 'Invite signup failed.');
      }

      setAuthStage('verifying_credentials');
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;

      setAuthStage('preparing_vault');

      // SUCCESS: Clear any old recovery flags so the modal doesn't pop up
      localStorage.removeItem('recovery_active');
      setAuthStage('opening_studio');
      
      toast.success(isSignUp ? 'Invite accepted. Welcome to Linkit.' : 'Welcome back to Linkit.');
      if (templateSlug) sessionStorage.setItem('linkit_pending_template', templateSlug);
      navigate(getPostAuthPath());
      await waitForRouteTransition();
    } catch (err) {
      setAuthStage('failed');
      toast.error(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
      setTimeout(() => setAuthStage('idle'), 1200);
    }
  };

  const validateInviteCode = async (code) => {
    const response = await fetch('/api/v1/invite-check', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ invite_code: code }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || 'Invite code is invalid.');
  };

  const handleOAuth = async (provider, verifiedInviteCode = '') => {
    if (isSignUp && !verifiedInviteCode) {
      setOauthInviteProvider(provider);
      setOauthInviteCode(inviteCode);
      setIsOAuthInviteOpen(true);
      return;
    }

    setAuthStage('authenticating');
    try {
      if (isSignUp && verifiedInviteCode) {
        sessionStorage.setItem(OAUTH_INVITE_STORAGE_KEY, verifiedInviteCode);
      }
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          // will handle the PKCE exchange and state validation automatically.
          redirectTo: `${window.location.origin}${getPostAuthPath()}`,
          queryParams: {
            ...(provider === 'google' ? { prompt: 'select_account' } : {}),
          },
          scopes: provider === 'google' ? 'email profile' : 'read:user user:email',
        },
      });
      if (error) throw error;
      // SUCCESS: Clear any old recovery flags so the modal doesn't pop up
      localStorage.removeItem('recovery_active');
      if (templateSlug) sessionStorage.setItem('linkit_pending_template', templateSlug);
      
      if (data?.url) window.location.href = data.url;
    } catch (err) {
      setAuthStage('failed');
      toast.error(err.message || `Could not connect with ${provider}`);
      setTimeout(() => setAuthStage('idle'), 1200);
    }
  };

  const submitOAuthInvite = async (event) => {
    event.preventDefault();
    if (!oauthInviteProvider) return;

    const normalizedCode = oauthInviteCode.toUpperCase().replace(/\s+/g, '').replace(/[^A-Z0-9-]/g, '').slice(0, 11);
    setIsCheckingInvite(true);
    try {
      await validateInviteCode(normalizedCode);
      setInviteCode(normalizedCode);
      setIsOAuthInviteOpen(false);
      await handleOAuth(oauthInviteProvider, normalizedCode);
    } catch (err) {
      toast.error(err.message || 'Invite code rejected.');
    } finally {
      setIsCheckingInvite(false);
    }
  };

  const handlePasskey = async () => {
    if (!capabilities?.platformAuthenticatorSupported) {
      toast.error('Passkey not available on this browser', {
        description: 'Use email or OAuth for now, then enable Hardware Vault in Security Settings.',
      });
      return;
    }

    setLoading(true);
    setAuthStage('checking_account');
    setPasskeyStage('preparing_passkey');
    try {
      await loginWithPasskey({ onStage: setPasskeyStage });
      setAuthStage('opening_studio');
      setPasskeyStage('complete');
      localStorage.removeItem('recovery_active');
      toast.success('Passkey verified', { description: 'Your Linkit session is open.' });
      if (templateSlug) sessionStorage.setItem('linkit_pending_template', templateSlug);
      navigate(getPostAuthPath());
      await waitForRouteTransition();
    } catch (err) {
      setAuthStage('failed');
      setPasskeyStage('failed');
      toast.error(err.message || 'Passkey sign-in failed');
    } finally {
      setLoading(false);
      setTimeout(() => {
        setAuthStage('idle');
        setPasskeyStage('idle');
      }, 1400);
    }
  };

  const authStageCopy = {
    idle: isSignUp ? 'Create account' : 'Sign in',
    checking_account: 'Checking account',
    verifying_credentials: 'Verifying credentials',
    preparing_vault: 'Preparing vault access',
    opening_studio: 'Opening your studio',
    failed: 'Try Again',
  }[authStage];

  const passkeyStageCopy = {
    idle: 'Continue with Passkey',
    preparing_passkey: 'Preparing secure challenge',
    waiting_passkey: 'Waiting for device approval',
    verifying_passkey: 'Verifying passkey signature',
    creating_session: 'Opening secure session',
    complete: 'Passkey accepted',
    failed: 'Try Passkey Again',
  }[passkeyStage] || 'Continue with Passkey';

  const isPasskeyBusy = loading && passkeyStage !== 'idle';

  const handleToggle = () => {
    gsap.to(cardRef.current, {
      opacity: 0.72,
      y: 6,
      duration: 0.12,
      ease: 'power2.out',
      onComplete: () => {
        setIsSignUp(s => !s);
        gsap.to(cardRef.current, { opacity: 1, y: 0, duration: 0.22, ease: 'power3.out' });
      },
    });
  };

  return (
    <div
      ref={containerRef}
      data-auth-mode={isSignUp ? 'signup' : 'login'}
      className="linkit-owned-page landing-page auth-nature-page min-h-dvh bg-edge-cream vibrant-grunge recycled-paper-overlay flex flex-col px-4 py-6 sm:p-8 selection:bg-edge-pink/30 relative overflow-x-hidden"
    >
      <style>{`
        input[type="password"]::-ms-reveal,
        input[type="password"]::-ms-clear {
          display: none;
        }
      `}</style>
      <div className="auth-nature-art" aria-hidden="true">
        <span className="auth-nature-light" />
        <span className="auth-nature-shadow auth-nature-shadow-left" />
        <span className="auth-nature-shadow auth-nature-shadow-right" />
        <span className="auth-nature-horizon" />
        <span className="auth-nature-mist auth-nature-mist-one" />
        <span className="auth-nature-mist auth-nature-mist-two" />
      </div>

      <div className="auth-shell w-full max-w-6xl m-auto relative z-10 py-4 sm:py-8">

        {/* Branding */}
        <div className="flex items-center justify-between gap-4 mb-7 sm:mb-10">
          <Link ref={logoRef} to="/" className="flex items-center gap-3 group pointer-events-auto">
            <div className="w-11 h-11 sm:w-12 sm:h-12 bg-edge-umber text-white flex items-center justify-center font-bold text-2xl shadow-brutalist-coral transition-transform">L</div>
            <span className="linkit-product-wordmark text-3xl sm:text-4xl text-edge-umber">linkit</span>
          </Link>
          <Link to="/" className="text-xs font-bold text-edge-umber/55 hover:text-edge-umber">Back to home</Link>
        </div>

        <div className="auth-layout grid min-w-0 gap-8 lg:grid-cols-[0.82fr_1.18fr] lg:items-center">
          <aside className="auth-editorial-panel">
            <span className="auth-editorial-kicker">{isSignUp ? 'Create your Linkit' : 'Return to your studio'}</span>
            <h2>{isSignUp ? 'One considered page for everything you make.' : 'Your work, audience, and next move—together.'}</h2>
            <p>
              {isSignUp
                ? 'Build a public page, offer direct support, understand what earns attention, and keep supported private content distinct.'
                : 'Continue shaping your page, managing your audience, and refining the routes that matter.'}
            </p>
            <div className="auth-editorial-features">
              <div><Layout size={18} /><span><strong>Design</strong>Arrange the page around your work</span></div>
              <div><BarChart2 size={18} /><span><strong>Understand</strong>See the signals that guide decisions</span></div>
              <div><ShieldCheck size={18} /><span><strong>Protect</strong>Keep public and private layers clear</span></div>
            </div>
          </aside>
          {/* Auth Card */}
          <div
            ref={cardRef}
            className="auth-card glass-surface-strong min-w-0 w-full max-w-2xl p-6 sm:p-10 lg:p-12 relative overflow-hidden"
          >
          <span className="auth-card-kicker">{isSignUp ? 'Account setup' : 'Secure sign in'}</span>
          <h1 className="text-4xl font-black mb-2 tracking-tight">
            {isSignUp ? 'Create your page' : 'Welcome back'}
          </h1>
          <p className="auth-card-intro text-edge-umber/60 mb-10 font-semibold text-sm">
            {isSignUp ? 'Use your invite, choose your identity, and open a workspace built around your page.' : 'Choose the sign-in method connected to your Linkit account.'}
          </p>

          {template && (
            <div className="mb-8 border-2 border-edge-umber bg-edge-cream p-4 text-center shadow-brutalist-amber">
              <p className="text-[10px] font-bold tracking-widest text-edge-umber/45">Starting with</p>
              <p className="mt-1 text-sm font-bold tracking-tight text-edge-umber">{template.label}</p>
            </div>
          )}

          {!isSignUp && (
            <div className="mb-8 space-y-4">
              <BrutalButton
                onClick={handlePasskey}
                disabled={loading}
                variant="pink"
                size="lg"
                className="w-full"
                shadow="brutalist"
                animation={isPasskeyBusy ? 'misprint' : 'none'}
              >
                {passkeyStage === 'complete' ? (
                  <CheckCircle2 size={18} />
                ) : isPasskeyBusy ? (
                  <RefreshCw size={18} className="animate-spin" />
                ) : (
                  <Lock size={18} />
                )}
                {isPasskeyBusy ? passkeyStageCopy : 'Continue with Passkey'}
              </BrutalButton>

              {isPasskeyBusy && (
                <BrutalCard shadow="dark" className="p-4 bg-slate-900 border-slate-900 text-white">
                  <p className="text-[10px] font-black uppercase tracking-widest text-white/60 mb-2">
                    {passkeyStageCopy}
                  </p>
                  <p className="text-[9px] font-bold uppercase tracking-tight text-white/40">
                    Keep the Windows Hello or security key prompt open until Linkit finishes the session handoff
                  </p>
                  <AuthProgress
                    steps={PASSKEY_AUTH_STAGES}
                    activeStage={passkeyStage}
                    failed={passkeyStage === 'failed'}
                    tone="dark"
                  />
                </BrutalCard>
              )}
            </div>
          )}

          <form onSubmit={handleAuth} className="space-y-8">

            <div className="login-field">
              <BrutalInput
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                shadow="dark"
              />
            </div>

            {isSignUp && (
              <div ref={usernameFieldRef} className="login-field">
                <BrutalInput
                  label="Username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                  placeholder="yourname"
                  required={isSignUp}
                  shadow="amber"
                  className={cn(
                    availability === 'taken' && "border-edge-poppy",
                    availability === 'available' && "border-green-500"
                  )}
                />
                <div className="mt-2 px-1">
                  {availability === 'verifying' && <p className="text-[8px] font-black uppercase text-slate-400">Checking username</p>}
                  {availability === 'available' && <p className="text-[8px] font-black uppercase text-green-600">Username available</p>}
                  {availability === 'taken' && <p className="text-[8px] font-black uppercase text-edge-poppy">Username taken</p>}
                </div>
              </div>
            )}

            {isSignUp && (
              <div className="login-field">
                <BrutalInput
                  label="Invite Code"
                  type="text"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value.toUpperCase().replace(/\s+/g, '').replace(/[^A-Z0-9-]/g, '').slice(0, 11))}
                  placeholder="INIT-9A4F2B"
                  required={isSignUp}
                  shadow="coral"
                  className="font-mono tracking-wider uppercase"
                />
                <p className="mt-2 px-1 text-[8px] font-black uppercase tracking-widest text-edge-umber/35">
                  One code opens one account
                </p>
              </div>
            )}

            <div className="login-field">
              <div className="relative group">
                <BrutalInput
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  shadow="pink"
                  className="pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '6px',
                    bottom: '6px',
                    zIndex: 50,
                    width: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'transparent',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    boxShadow: 'none',
                    padding: 0,
                    outline: 'none',
                    appearance: 'none',
                    transition: 'background-color 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(60, 42, 33, 0.06)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    style={{ overflow: 'visible' }}
                  >
                    {/* Eye outline */}
                    <path
                      d="M2.5 12C2.5 12 5.5 5 12 5C18.5 5 21.5 12 21.5 12C21.5 12 18.5 19 12 19C5.5 19 2.5 12 2.5 12Z"
                      stroke={showPassword ? '#3C2A21' : '#3C2A21'}
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill="none"
                      style={{
                        opacity: showPassword ? 0.7 : 0.35,
                        transition: 'opacity 0.35s ease',
                      }}
                    />
                    {/* Iris ring */}
                    <circle
                      cx="12"
                      cy="12"
                      r="3.5"
                      stroke={showPassword ? '#FF1E90' : '#3C2A21'}
                      strokeWidth="1.8"
                      fill="none"
                      style={{
                        opacity: showPassword ? 0.8 : 0.25,
                        transform: `scale(${showPassword ? 1 : 0.6})`,
                        transformOrigin: '12px 12px',
                        transition: 'all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)',
                      }}
                    />
                    {/* Pupil dot */}
                    <circle
                      cx="12"
                      cy="12"
                      r="1.5"
                      fill={showPassword ? '#FF1E90' : '#3C2A21'}
                      style={{
                        opacity: showPassword ? 1 : 0.15,
                        transform: `scale(${showPassword ? 1 : 0})`,
                        transformOrigin: '12px 12px',
                        transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                        transitionDelay: showPassword ? '0.08s' : '0s',
                      }}
                    />
                    {/* Strike-through line */}
                    <line
                      x1="4"
                      y1="4"
                      x2="20"
                      y2="20"
                      stroke="#3C2A21"
                      strokeWidth="2"
                      strokeLinecap="round"
                      style={{
                        opacity: showPassword ? 0 : 0.55,
                        strokeDasharray: '24',
                        strokeDashoffset: showPassword ? '24' : '0',
                        transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    />
                  </svg>
                </button>
              </div>
              {!isSignUp && (
                <div className="flex justify-end mt-2">
                  <button
                    type="button"
                    onClick={handleResetPassword}
                    className="text-xs font-bold text-edge-pink hover:underline"
                  >
                    Forgot password?
                  </button>
                </div>
              )}
            </div>

            <BrutalButton
              type="submit"
              variant={isSignUp ? 'pink' : 'amber'}
              size="lg"
              className="w-full"
              shadow="brutalist"
              disabled={loading}
              animation={loading ? 'misprint' : 'none'}
            >
              {!loading && <ArrowRight size={20} />}
              {loading ? authStageCopy : (isSignUp ? 'Create account' : 'Sign in')}
            </BrutalButton>
            {loading && !isPasskeyBusy && (
              <AuthProgress
                steps={PASSWORD_AUTH_STAGES}
                activeStage={authStage}
                failed={authStage === 'failed'}
              />
            )}
          </form>

          {/* Divider + OAuth */}
          <div className="login-oauth-section">
            <div className="relative my-12">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-edge-umber/10" /></div>
              <div className="relative flex justify-center text-[10px] font-bold tracking-[0.12em]"><span className="bg-white px-6 text-edge-umber/35">Or continue with</span></div>
            </div>

            <div className="space-y-4">
              <BrutalButton
                onClick={() => handleOAuth('google')}
                variant="default"
                size="md"
                className="w-full"
                shadow="dark"
              >
                <GoogleLogo /> Continue with Google
              </BrutalButton>
              <BrutalButton
                onClick={() => handleOAuth('github')}
                variant="dark"
                size="md"
                className="w-full"
                shadow="brutalist"
              >
                <GithubLogo /> Continue with GitHub
              </BrutalButton>
            </div>
          </div>

          <p className="login-toggle-text mt-10 text-center text-edge-umber/50 font-semibold text-sm">
            {isSignUp ? 'Already a member?' : 'New here?'}{' '}
            <button
              onClick={handleToggle}
              className="text-edge-pink hover:underline underline-offset-4"
            >
              {isSignUp ? 'Log In' : 'Sign Up'}
            </button>
          </p>
          </div>
        </div>
      </div>

      {isOAuthInviteOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-edge-umber/60 p-4 backdrop-blur-sm">
          <form
            onSubmit={submitOAuthInvite}
            className="w-full max-w-md glass-surface-strong p-6 sm:p-8"
          >
            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.24em] text-edge-pink">Linkit invite</p>
                <h2 className="mt-2 text-3xl font-black uppercase tracking-tight text-edge-umber">Enter invite code</h2>
                <p className="mt-2 text-sm font-bold leading-relaxed text-edge-umber/60">
                  Verify your invite before continuing with {oauthInviteProvider === 'github' ? 'GitHub' : 'Google'}.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsOAuthInviteOpen(false)}
                className="flex h-10 w-10 shrink-0 items-center justify-center border-2 border-edge-umber bg-edge-cream text-edge-umber transition hover:bg-edge-umber hover:text-white"
                aria-label="Close invite modal"
              >
                <X size={18} />
              </button>
            </div>

            <BrutalInput
              label="Invite Code"
              type="text"
              value={oauthInviteCode}
              onChange={(e) => setOauthInviteCode(e.target.value.toUpperCase().replace(/\s+/g, '').replace(/[^A-Z0-9-]/g, '').slice(0, 11))}
              placeholder="INIT-9A4F2B"
              required
              shadow="dark"
            />

            <BrutalButton
              type="submit"
              variant="pink"
              size="lg"
              className="mt-6 w-full"
              shadow="brutalist"
              disabled={isCheckingInvite}
              animation={isCheckingInvite ? 'misprint' : 'none'}
            >
              {isCheckingInvite ? <RefreshCw size={18} className="animate-spin" /> : <ArrowRight size={18} />}
              {isCheckingInvite ? 'Checking invite...' : 'Continue'}
            </BrutalButton>
          </form>
        </div>
      )}

      <PasswordResetModal 
        isOpen={isPasswordResetOpen}
        onClose={() => setIsPasswordResetOpen(false)}
      />
    </div>
  );
};

export default Login;
